import { useMemo, type FC } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { ExternalLink } from 'src/components/navigation/link';
import { Heading } from 'src/components/text/heading';

/**
 * This component doesn't use dangerouslySetInnerHTML,
 * but rehypeSanitize ensures that only safe HTML is rendered.
 *
 * Only use this behind lazy loaded pages, because the libs are quite big.
 */
export const Markdown: FC<{ content: string; 'data-testid'?: string }> = ({
  content,
  'data-testid': dataTestId,
}) => {
  const modifiedText = useMemo(() => {
    const lines = content.split('\n');

    return lines
      .map((line, index) => {
        // Check if the line is part of a list
        const isListItem = /^\s*[*\-+]\s+|^\s*\d+\.\s+/.test(line);
        const isNextLineListItem =
          index < lines.length - 1 &&
          /^\s*[*\-+]\s+|^\s*\d+\.\s+/.test(lines[index + 1]);

        if (isListItem || isNextLineListItem) return line;

        if (line.trim() === '\\') return line.replace('\\', '&nbsp;\n');

        return line + '&nbsp;\n';
      })
      .join('\n');
  }, [content]);
  return (
    <div data-testid={dataTestId}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          h1: ({ children }) => (
            <Heading mode="headline" size="lg">
              {children}
            </Heading>
          ),
          h2: ({ children }) => (
            <Heading mode="headline" size="md">
              {children}
            </Heading>
          ),
          h3: ({ children }) => (
            <Heading mode="title" size="md">
              {children}
            </Heading>
          ),
          h4: ({ children }) => (
            <Heading mode="title" size="sm">
              {children}
            </Heading>
          ),
          h5: ({ children }) => <strong>{children}</strong>,
          h6: ({ children }) => <strong>{children}</strong>,
          a: ({ href, children }) => (
            <ExternalLink
              data-testid={`markdown-link-${href}`}
              mode="default-underlined"
              href={href}
              target="_blank"
            >
              {children}
            </ExternalLink>
          ),
        }}
      >
        {modifiedText}
      </ReactMarkdown>
    </div>
  );
};
